import { useQuery } from '@apollo/client';
import React from 'react';
import useAppSelector from '../app/redux/useAppSelector';
import SVGIcon from '../components/SVGIcon';
import PopOver from '../components/deprecated/PopOver';
import PopOverContainer from '../components/deprecated/PopOverContainer';
import { PostType } from '../graphql/generated/graphql';
import verifiedIcon from '../icons/non-prefilled/16x16/verified.svg';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import yearSensitiveDateString from '../utils/yearSensitiveDateString';
import PostStateBarOverflowMenu from './PostStateBarOverflowMenu';
import styles from './PostVerificationExpiredBar.module.scss';
import PostViewStateBar from './PostViewStateBar';
import VerificationEntries from './VerificationEntries';
import VerifyMenuItems, {
  useShouldRenderVerifyMenuItems,
} from './VerifyMenuItems';
import { isVerifier } from './postPolicyCheck';
import useStartReverification from './useStartReverification';
import Button from '../components/Button';
import VerifyHistoryMenuItem from './VerifyHistoryMenuItem';
import { gql } from '../graphql/generated/gql';

const POST_QUERY = gql(`
  query PostVerificationExpiredBar($id: ID!) {
    post(id: $id) {
      id
      type
      verificationEntries {
        id
        expiredAt
        version
        ...VerificationEntries_verificationEntries
      }
      ...isVerifier_post
      ...VerifyMenuItems_post
      ...VerifyHistoryMenuItem_post
      ...useStartReverification_post
    }
  }
`);

interface PostVerificationExpiredBarProps {
  postId: string;
}

const PostVerificationExpiredBar = ({
  postId,
}: PostVerificationExpiredBarProps) => {
  const shouldRenderVerifyMenuItems = useShouldRenderVerifyMenuItems();
  const isMobile = useIsSize(Sizes.SM);
  const user = useAppSelector((state) => state.session.user);
  const { data } = useQuery(POST_QUERY, { variables: { id: postId } });
  const post = data?.post;
  const { loading, startReverification } = useStartReverification(post);

  if (!post?.verificationEntries[0]) return null;
  const isUserAllowedToVerify = isVerifier(post, user);

  return (
    <PostViewStateBar
      actions={
        (shouldRenderVerifyMenuItems({ post, user }) ||
          isUserAllowedToVerify) &&
        (isUserAllowedToVerify ? (
          <Button
            variant="secondary"
            size="small"
            disabled={loading}
            onClick={startReverification}
          >
            Verify
          </Button>
        ) : (
          <PostStateBarOverflowMenu variant="warning">
            <VerifyMenuItems post={post} />
            <VerifyHistoryMenuItem post={post} />
          </PostStateBarOverflowMenu>
        ))
      }
    >
      <SVGIcon>{verifiedIcon}</SVGIcon>
      {/* @ts-expect-error */}
      <PopOver
        display="inline-block"
        popOverPortal={document.body}
        placement="bottom-start"
        popOver={
          <PopOverContainer width="wide">
            <div className={styles.previousVerifications}>
              Previous verifications
            </div>
            <VerificationEntries
              verificationEntries={post.verificationEntries}
            />
          </PopOverContainer>
        }
        trigger="hover"
      >
        <strong>
          {post.type === PostType.Template
            ? 'Template Verification'
            : 'Verification'}
        </strong>{' '}
        expired
        {!isMobile &&
          ` on ${yearSensitiveDateString(
            post.verificationEntries[0].expiredAt,
          )}`}
      </PopOver>
    </PostViewStateBar>
  );
};

export default PostVerificationExpiredBar;
